import { withHandlers, withProps, withState, compose } from 'recompose'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'

import * as PlaybookActions from 'app/modules/playbook/actions'

import { EditorCtaButtonModal, coerceToValidHex } from './component'
import { Widths, Alignments, SpacingSizes, ButtonEditTabs, LinkToTypes, PixelSizes } from './constants'


const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      hideCtaButtonModal: PlaybookActions.hideCtaButtonModal,
      allChanges: PlaybookActions.allChanges,
    },
    dispatch
  ),
})


/*
 * takes a string that looks like:
 * rgb(155, 209, 71)
 * and returns a string that looks like:
 * #e1aa00
 */
const rgbStringToHexString = (rgbString) => {
  const hexString = rgbString
    // now we have:
    // "rgb(155, 209, 71)""
    .split(', ')
    // ["rgb(155", "209", "71)"]
    .map(val => val.replace(/(rgb\()?(\))?/g, ''))
    // ["155", "209", "71"]
    .map(Number)
    // [155, 209, 71]
    .map(i => i.toString(16))
    // ["9b", "d1", "47"]
    .join('')
    // "9bd147"
  return `#${hexString}`
}

const coerceColor = (colorString, defaultColor = '#eeeeee') => {
  if (!colorString) {
    return defaultColor
  } else if (colorString.startsWith('#')) {
    return coerceToValidHex(colorString)
  } else if (colorString.startsWith('rgb(')) {
    return rgbStringToHexString(colorString)
  } else {
    return defaultColor
  }
}


const linkTypeToWebLabel = {
  [LinkToTypes.EMAIL]: { label: 'Email', placeholder: 'steve@apple.com' },
  [LinkToTypes.WEB_ADDRESS]: { label: 'Web Address (URL)', placeholder: 'https://www.drift.com' },
}

const camelCaseToDashCase = (camelStr) => camelStr.replace(/([A-Z])/g, (g) => `-${g[0].toLowerCase()}`)

const reactStyleToCssStyle = (reactStyle) => {
  return _.map(reactStyle, (val, key) => {
    return [camelCaseToDashCase(key), val]
  }).reduce((prev, [styleName, styleVal]) => {
    return `${prev}${styleName}:${styleVal};`
  }, '')
}


export default compose(
  connect(null, mapDispatchToProps),
  withProps(() => {
    // attempt to pull the button out of the email content
    const oldBtnContainer = window.tinymce.activeEditor.getBody().querySelector('div[data-drift-bui]')
    const btnContainer = window.tinymce.activeEditor.getBody().querySelector('div[data-drift-cta-container]')
    if (!btnContainer && !oldBtnContainer)
      return {
        existingBtnFound: false
      }
    const btnElt = !!oldBtnContainer ? oldBtnContainer.children[0] : btnContainer.children[0]
    return {
      existingBtnFound: true,
      initialBtnContainerElt: !!btnContainer ? btnContainer : oldBtnContainer,
      initialBtnStyle: btnElt.style,
      initialBtnContainerStyle: !!btnContainer ? btnContainer.style : oldBtnContainer.style,
      initialBtnCopy: btnElt.innerText,
      initialBtnLink: btnElt.href,
    }
  }),
  withState('buttonAlignment', 'setButtonAlignment', props => {
    const alignment = _.get(props, 'initialBtnContainerStyle.textAlign', Alignments[0])
    return `${alignment[0].toUpperCase()}${alignment.slice(1)}`
  }),
  withState('buttonWidth', 'setButtonWidth', props => _.get(props, 'initialBtnStyle.width', Widths['No Change'])),
  withState('buttonCopy', 'setButtonCopy', props => props.initialBtnCopy || 'Click Me!'),
  withState('linkValue', 'setLinkValue', props => props.initialBtnLink || ''),
  withState('linkToType', 'setLinkToType', props => (
    _.get(props, 'initialBtnLink', '').toLowerCase().startsWith('mailto:') ? LinkToTypes.EMAIL : LinkToTypes.WEB_ADDRESS)
  ),
  withState('borderRadius', 'setBorderRadius', props => _.get(props, 'initialBtnStyle.borderTopRightRadius', '3px').replace('px', '')),
  withState('bgColorHex', 'setBgColorHex', props => coerceColor(_.get(props, 'initialBtnStyle.backgroundColor'))),
  withState('textColorHex', 'setTextColorHex', props => coerceColor(_.get(props, 'initialBtnStyle.color'), '#000000')),
  withState('textSize', 'setTextSize', props => _.get(props, 'initialBtnStyle.fontSize', PixelSizes[2])),
  withState('letterSpacing', 'setLetterSpacing', props => _.get(props, 'initialBtnStyle.letterSpacing', SpacingSizes.Normal)),
  withState('paddingSize', 'setPaddingSize', props => _.get(props, 'initialBtnStyle.paddingRight', PixelSizes[2])),
  withState('activeEditTab', 'setActiveEditTab', ButtonEditTabs.CONTENT),
  withState('displayBgColorPicker', 'setDisplayBgColorPicker', false),
  withState('displayTextColorPicker', 'setDisplayTextColorPicker', false),
  withProps(props => {
    let previewButtonLink = props.linkValue
    if (props.linkToType === LinkToTypes.EMAIL) {
      previewButtonLink = `mailto:${previewButtonLink}`
    } else {
      const protocolRe = RegExp('^https?:\/\/*')
      if (!protocolRe.test(props.linkValue)) {
        previewButtonLink = `https://${previewButtonLink}`
      }
    }
    return {
      onContentTabClick: () => props.setActiveEditTab(ButtonEditTabs.CONTENT),
      onStyleTabClick: () => props.setActiveEditTab(ButtonEditTabs.STYLE),
      onSettingsTabClick: () => props.setActiveEditTab(ButtonEditTabs.SETTINGS),
      previewButtonLink,
      urlInputSetup: linkTypeToWebLabel[props.linkToType],
      buttonStyle: {
        borderRadius: `${props.borderRadius}px`,
        backgroundColor: coerceToValidHex(props.bgColorHex),
        color: props.textColorHex,
        padding: props.paddingSize,
        fontSize: props.textSize,
        letterSpacing: props.letterSpacing,
        width: props.buttonWidth,
        textAlign: 'center',
        display: 'inline-block',
        textDecoration: 'none',
      },
      buttonContainerStyle: { textAlign: props.buttonAlignment },
    }
  }),
  withHandlers({
    hideCtaButtonModal: props => () => {
      // clear out the state
      props.actions.hideCtaButtonModal()
    },
    onInsert: props => () => {
      const editor = window.tinymce.activeEditor
      const buttonStyle = reactStyleToCssStyle(props.buttonStyle)
      const buttonContainerStyle = reactStyleToCssStyle(props.buttonContainerStyle)
      const btn = `<a target="_blank" data-drift-bui href=${props.previewButtonLink} style="${buttonStyle}">${props.buttonCopy}</a>`

      if (props.existingBtnFound) {
        const wrapperElt = document.createElement('div')
        wrapperElt.style = buttonContainerStyle
        wrapperElt.classList.add('drift-cta-button')
        wrapperElt.setAttribute('data-drift-cta-container', null)
        wrapperElt.innerHTML = btn
        editor.dom.replace(wrapperElt, props.initialBtnContainerElt)
      } else {
        const insertedContent = `<div class="mceNonEditable"><div class="drift-cta-button" data-drift-cta-container style="${buttonContainerStyle}">${btn}</div></div>`
        editor.execCommand('mceInsertContent', false, insertedContent)
      }
      props.actions.allChanges()
      props.actions.hideCtaButtonModal()
    },
    openBgColorPicker: props => () => {
      props.setDisplayBgColorPicker(true)
    },
    closeBgColorPicker: props => () => {
      props.setDisplayBgColorPicker(false)
    },
    openTextColorPicker: props => () => {
      props.setDisplayTextColorPicker(true)
    },
    closeTextColorPicker: props => () => {
      props.setDisplayTextColorPicker(false)
    },

  }),
)(EditorCtaButtonModal)



// WEBPACK FOOTER //
// ./src/scripts/components/playbook/components/EditorCtaButtonModal/index.js